import { Box, Button, Typography, useTheme } from '@mui/material'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import React, { useMemo } from 'react'
import { observer } from 'mobx-react'

import { generateMeetingGrid, indexToName } from '../../utils/meeting-utilities'
import { useToggleButtonsData } from '../../hooks/useToggleButtonsData'
import { useSocketsContext } from '../../contexts/sockets.context'
import { useMeetingStore } from '../../store/meeting.store'
import VirtualboardManager from './VirtualboardManager'
import useWindow from '../../hooks/useWindow'
import CustomSwitch from '../common/CustomSwitch'
import UserCard from './UserCard'
import { Icons } from '../../utils/utilities'
import { useMeetingActionsContext } from '../../contexts/meetingactions.context'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'


const sharingGridTemplates = {}

function sortUsers ( users ) {
  return [...users].sort( ( a, b ) => {
    // weight function gives priority score

    const weight = ( u ) => {

      if ( u.type === 'local-client' ) return 1
      if ( u.activePresenter ) return 70
      if ( u.isSharing ) return 60
      if ( u.isVideoOn ) return 50
      if ( u.isAudioOn ) return 40
      if ( u.pinned ) return 30
      return 20
    }

    return weight( b ) - weight( a ) // higher weight first
  } )
}

const getBalancedLayoutAreas = () => {
  const width = window.innerWidth
  const height = window.innerHeight

  if ( width > height )
    return `
        "a a a a b b"
        "a a a a b b"
        `
  return `
      "a a"
      "a a"
      "a a"
      "b b"
      "b b"
      `
}
const getFocusedLayoutAreas = () => {
  const width = window.innerWidth
  const height = window.innerHeight

  if ( width > height )
    return `
      "a a a a a b"
      "a a a a a ."
      "a a a a a ."
      `
  return {
    sm: `
    "a a a a a a a"
    "a a a a a a a"
    "a a a a a a a"
    ". . b b b . ."
      `,
    xs: `
    "a a a a a a a"
    "a a a a a a a"
    "a a a a a a a"
    "b b b b b b b"
      `
  }

}

const HybridClassParticipationLayout = observer( () => {

  const { width: windowWidth } = useWindow()
  const MeetingStore = useMeetingStore()

  const { palette, border } = useTheme()
  const { meeting_id } = useParams()

  const { toggleScreenShare, toggleScreenShareAudio } = useMeetingActionsContext()
  const { raiseHandButtonDetails } = useToggleButtonsData()
  // const { socket, emitEvent } = useSocketsContext()
  const OnlineSessionSocketStore = useOnlineSessionsSocketStore()

  const openParticipantsList = () => {
    const openFlag = MeetingStore.getMeetingRoomLayoutSettings?.leftSideCollapseTab !== 'participants' ? true : !MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen
    MeetingStore.setMeetingRoomLayoutSettings( {
      leftSideCollapseTab: 'participants',
      isLeftSideCollapseOpen: openFlag
    } )
  }

  const raiseHand = async () => {
    if ( MeetingStore.getMeetingState.raisedHand ) {
      const { success, message } = await OnlineSessionSocketStore.emit( "handraises:lower", { room_id: meeting_id } )
      if ( success )
        MeetingStore.setMeetingState( { raisedHand: false } )
      if ( message )
        toast( message )
      return
    }

    const { success, message } = await OnlineSessionSocketStore.emit( "handraises:raise", { room_id: meeting_id } )
    if ( success )
      MeetingStore.setMeetingState( { raisedHand: true } )
    if ( message )
      toast( message )
  }

  const getUsersCard = useMemo( () => {
    const data = {}
    const filtered = [...MeetingStore.getRemoteUsers, MeetingStore.getLocalUser].filter( user => user.isVideoOn || user.isAudioOn || user.meeting_role === 'meeting-host' )

    const sortedUsers = sortUsers( filtered )

    const { presentingVirtualBoard, activePresenter, isSomeoneSharing, isSharing, layout = {} } = MeetingStore.getMeetingState
    const { layoutMode = "balanced" } = layout


    data.users = [...sortedUsers.slice( 0, sortedUsers.length >= 49 ? 48 : 49 )]
    data.presentingVirtualBoard = presentingVirtualBoard

    if ( MeetingStore.getLocalUser.meeting_role === "meeting-host" )
      data.showEmptyBoard = !( data.users[0] && ( data.users[0]?.sharing || data.users[0]?.type === 'share-client' ) ) && !data.presentingVirtualBoard
    else
      data.showEmptyBoard = false


    data.extraIndex = ( ( data.showEmptyBoard || data.presentingVirtualBoard ) ? 1 : 0 )
    data.cards = data.users.length + ( ( data.showEmptyBoard || data.presentingVirtualBoard ) ? 1 : 0 )
    data.showExtraUsersCard = sortedUsers.length > 49
    data.sharingLayout = isSharing || isSomeoneSharing || Boolean( activePresenter )
    data.isActivePresenter = activePresenter?.user_id === `${MeetingStore.getLocalUser.user_id}-sharing` || activePresenter?.user_id === MeetingStore.getLocalUser.user_id
    data.activePresenter = activePresenter
    const styles = ( data.presentingVirtualBoard || data.showEmptyBoard ) ? layoutMode === "balanced" ?
      { areas: getBalancedLayoutAreas() } : layoutMode === "floating" ? { areas: "'a'" } : { areas: getFocusedLayoutAreas() } :
      generateMeetingGrid( data.cards, { width: windowWidth, isPresenting: data.sharingLayout, presenterRatio: 1 / 2 } )
    data.areasLayout = styles.areas
    return data
  }, [MeetingStore.getMeetingState, MeetingStore.getRemoteUsers, MeetingStore.getLocalUser, windowWidth] )

  // const getUsersCard = useMemo( () => {
  //   const data = {}
  //   const users = []
  //   for ( let index = 0; index < 1; index++ ) {
  //     users.push( { name: `User #${index + 1}`, user_id: index + 1, color_index: index % 19, isAudioOn: false, isVideoOn: false, color_index: Math.floor( Math.random() * 20 ) } )
  //   }

  //   data.presentingVirtualBoard = true
  //   data.showEmptyBoard = false
  //   data.users = [...users]
  //   data.extraIndex = data.presentingVirtualBoard ? 1 : 0
  //   // const isHost = MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id )

  //   // if ( isHost ) {
  //   //   data.users.push( MeetingStore.getLocalUser )
  //   // }

  //   data.cards = data.users.length + ( data.showExtraUsersCard ? 1 : 0 ) + ( data.showEmptyBoard ? 1 : 0 )


  //   data.sharingLayout = false
  //   // const styles = generateFactorGrid( data.cards, { width: window.innerWidth, maxCols: 5 } )
  // const styles = data.presentingVirtualBoard ? layout === "balanced" ?
  //   { areas: getBalancedLayoutAreas() } : { areas: getFocusedLayoutAreas() } :
  //   generateMeetingGrid( data.cards, { width: windowWidth, isPresenting: data.sharingLayout, presenterRatio: 1 / 2 } )

  //   data.areasLayout = styles.areas

  //   data.extraUsers = users.length - 49
  //   return data
  // }, [MeetingStore.getMeetingState, MeetingStore.getLocalUser, windowWidth, layout] )


  return (
    <Box minHeight={0} height="100%" flexShrink={1} display="flex" flexDirection="column" id="users-container" flexGrow={1} gap="10px" >
      {getUsersCard.sharingLayout && getUsersCard.activePresenter && <Box borderRadius="10px" bgcolor={palette.rowHover} padding="10px 15px">
        {getUsersCard.isActivePresenter && <Box color={palette.greyed.contrastText} display="flex" justifyContent="space-between" alignItems="center" gap="10px">
          <Box display="flex" alignItems="center" fontSize="16px" gap="10px">
            {Icons.default.PresentToAll}
            <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px">You are presenting</Typography>
          </Box>
          {MeetingStore.getMeetingState.sharingState.audio && <Box display="flex" alignItems="center" gap="10px">
            <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px">Share audio</Typography>
            <CustomSwitch color="secondary" size="small" checked={MeetingStore.getMeetingState.sharingState.audioEnabled} onChange={event => {
              toggleScreenShareAudio( event.target.checked )
            }} />
          </Box>}
        </Box>}
        {!getUsersCard.isActivePresenter && <Box>
          <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px" display="block" >{getUsersCard?.activePresenter?.name || getUsersCard?.activePresenter?.user_name} is presenting</Typography>
        </Box>}
      </Box>}
      {MeetingStore.getMeetingState.isJoined && <Box minHeight={0} display="flex" flexGrow={1} flexDirection={MeetingStore.getMeetingState.isSharing ? "row" : "column"}  >
        <Box
          position="relative"
          id="users-grid"
          display="grid"
          padding="5px"
          gridTemplateAreas={
            getUsersCard.areasLayout
          }
          gridAutoRows="1fr"
          gridAutoColumns="1fr"
          gap="10px"
          flexGrow={1}
          overflow="auto"
          sx={{
            transition: `all 1s ease`
          }}
        >
          {getUsersCard.presentingVirtualBoard && MeetingStore.getPresentingDocument?.slides && <Box borderRadius="20px" gridArea="a">
            <VirtualboardManager />
          </Box>}
          {/* {getUsersCard.presentingVirtualBoard && MeetingStore.getPresentingDocument?.slides && <Box bgcolor="grey" borderRadius="10px" gridArea="a">

          </Box>} */}


          {getUsersCard.showEmptyBoard && <Box flexGrow={1} borderRadius="20px" border={border[1]} bgcolor={palette.contentBg + "66"} padding="20px" display="flex" flexDirection="column" gap="10px" alignItems="center" justifyContent="center" sx={{ gridArea: "a" }} >
            <Typography textAlign="center" component="span" variant='h6'>Nothing is being shared right now!</Typography>
            <Typography textAlign="center" component="span" variant='subtitle2' maxWidth="450px" color="textSecondary">
              {MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) ? "Start sharing your sceen to present to the participants." : "Please wait for the meeting host to share something, or you can ask permission to share by raising your hand."}
            </Typography>
            <Box display="flex" justifyContent="center" gap="10px" flexWrap="wrap" alignItems="center">
              {MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) && <Button variant='outlined' sx={{ flexGrow: 1, borderRadius: "20px" }} color='secondary' onClick={() => MeetingStore.setMeetingRoomLayoutSettings( { isPrentationsManagerOpen: true } )} startIcon={Icons.default.FilePresent}>Start Presenting File</Button>}
              {!MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) && <Button variant='outlined' sx={{ flexGrow: 1, borderRadius: "20px" }} color='greyed' onClick={raiseHand} startIcon={raiseHandButtonDetails.icon}>{raiseHandButtonDetails.isOn ? "Lower hand" : "Raise Hand"}</Button>}
            </Box>
          </Box>}

          {MeetingStore.getMeetingState?.layout?.layoutMode !== 'floating' && getUsersCard.users.map( ( user, index ) => (
            <UserCard toggleScreenShare={toggleScreenShare} gridArea={indexToName( index + getUsersCard.extraIndex )} key={user.user_id} user={user} />
          )
          )}

          {MeetingStore.getMeetingState?.layout?.layoutMode === "floating" && getUsersCard.users.length > 0 &&
            <Box position="absolute" top="10px" right="10px" zIndex={100} width="200px" height="200px">
              <UserCard toggleScreenShare={toggleScreenShare} gridArea={""} key={getUsersCard.users[0].user_id} user={getUsersCard.users[0]} />
            </Box>
          }

          {getUsersCard.showExtraUsersCard && <Box
            onClick={openParticipantsList}
            borderRadius="20px"
            sx={{
              background: `radial-gradient(circle, #0e3852ff, #4d1845ff)`,
              cursor: "pointer",
              gridArea: indexToName( getUsersCard.users.length )
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography color="white" variant="h6" textAlign="center">+ {getUsersCard.extraUsers} users</Typography>
          </Box>}
          {/* <UserCard gridArea={String.fromCharCode( 97 + index + ( MeetingStore.getMeetingState.isSharing ? 1 : 0 ) )} key={index} user={user} /> */}
        </Box>
      </Box>}
    </Box>
  )
} )

export default HybridClassParticipationLayout
